<template>
  <app-form ref="form" :schema="schema" :layout="layout" :data="formModel" @on-submit="onSubmit"
    @on-input="onFormFieldChanged" @on-input-modified="onFormFieldModified">
    <template v-slot:title>
      <h4 class="mb-0 text-uppercase font-weight-bolder">{{ invoiceTitle }}</h4>
      <sub>{{ currentDate }}</sub>
    </template>
    <template v-slot:spacing-seller>
      <app-form-sub-title>
        <template v-slot:left-content>
          <button type="button" @click="showSellerInfo = !showSellerInfo" class="btn btn-link mr-2">
            <i class="fas fa-caret-right primary-color" :class="{ 'fa-rotate-90': showSellerInfo }"></i>
          </button>
        </template>
        {{ $t('invoice.sellerInformation') }}
      </app-form-sub-title>
    </template>
    <template v-slot:spacing-buyer>
      <app-form-sub-title>
        <template v-slot:left-content>
          <button type="button" @click="showBuyerInfo = !showBuyerInfo" class="btn btn-link mr-2">
            <i class="fas fa-caret-right primary-color" :class="{ 'fa-rotate-90': showBuyerInfo }"></i>
          </button>
        </template>
        {{ $t('invoice.buyerInformation') }}
      </app-form-sub-title>
    </template>
    <template v-slot:spacing-delivery>
      <transition name="slide-fade">
        <hr v-if="showDeliveryInfo" style="margin: 1.875rem 0" />
      </transition>
    </template>
  </app-form>
</template>

<script>
import { mapState } from 'vuex';
import InvoiceProxy from '@/store/proxies/invoice-proxy';
import { parseFormOption } from '@/utils/select-options';
import { regexTaxCode, regexNationalId, regexPassportNo, regexMdvqhnsach } from '@/utils/regex';
import AppForm from '@/components/app-form/';
import AppFormSubTitle from '@/components/app-form/form-sub-title';
import { BUYER_TYPE } from '../../app-constant';
import SettingsProxy from '../../store/proxies/e-invoice/settings-proxy';

const defaultFormModel = {
  seller_name: '',
  ignore_buyer: false,
  seller_taxcode: '',
  seller_address: '',
  seller_bank_account: '',
  seller_bank_name: '',
  buyer_name: '',
  buyer_legal_name: '',
  buyer_type: BUYER_TYPE.ENTERPRISE,
  buyer_taxcode: '',
  buyer_id_no: '',
  buyer_passport_no: '',
  buyer_address: '',
  buyer_phone_number: '',
  buyer_fax_number: '',
  buyer_email: '',
  mdvqhnsach: '',
  payment_method: 'CK',
  currency_code: 'VND',
  invoice_type: '',
  template_code: '',
  invoice_common_code: '',
  kind_of: true,
  id: null,
  tags: [],
  lines: [],
  NBan_LDDNBo: '',
  NBan_PTVChuyen: '',
  NBan_HDSo: '',
  NBan_DChi: '',
  NBan_TNVChuyen: '',
  NBan_HVTNXHang: '',
};

const formSchema = context => ({
  einvoice_provider: {
    label: context.$t('user.eAccountProvider'),
    placeholder: `${context.$t('app.enter')} ${context.$t('user.eAccountProvider')}`,
    type: 'select',
    options: [],
  },
  template_code: {
    label: context.$t('invoice.templateCode'),
    placeholder: `${context.$t('app.choose')} ${context.$t('invoice.templateCode')}`,
    type: 'select',
    options: context.templateCodeOptions,
    validation: 'required',
    horizontalLayout: true,
    className: 'width-10rem',
  },
  payment_method: {
    label: context.$t('invoice.paymentMethod'),
    placeholder: `${context.$t('app.choose')} ${context.$t('invoice.paymentMethod')}`,
    type: 'select',
    horizontalLayout: true,
    className: 'width-10rem',
    options: context.paymentMethodOptions,
    validation: 'required',
    display: () => context.showBuyerInfo,
  },
  currency_code: {
    label: context.$t('invoice.currencyCode'),
    placeholder: `${context.$t('app.choose')} ${context.$t('invoice.currencyCode')}`,
    type: 'select',
    horizontalLayout: true,
    options: context.currencyCodeOptions,
    validation: 'required',
    display: () => context.showBuyerInfo,
  },
  invoice_type: {
    label: context.$t('invoice.invoiceType'),
    placeholder: `${context.$t('app.choose')} ${context.$t('invoice.invoiceType')}`,
    type: 'select',
    options: context.invoiceTypeOptions,
    validation: 'required',
    horizontalLayout: true,
    className: 'width-10rem',
  },
  invoice_common_code: {
    label: context.$t('invoice.invoiceCommonCode'),
    placeholder: `${context.$t('app.choose')} ${context.$t('invoice.invoiceCommonCode')}`,
    type: 'select',
    options: context.commonCodeOptions,
    validation: 'required',
    horizontalLayout: true,
    className: 'width-10rem',
  },
  seller_name: {
    label: context.$t('invoice.seller_name'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.seller_name')}`,
    type: 'input',
    validation: 'required',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    disabled: true,
    display: () => context.showSellerInfo,
  },
  seller_address: {
    label: context.$t('invoice.seller_address'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.seller_address')}`,
    type: 'input',
    validation: 'required',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    disabled: true,
    display: () => context.showSellerInfo,
  },
  seller_taxcode: {
    label: context.$t('invoice.seller_taxcode'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.seller_taxcode')}`,
    type: 'input',
    validation: { required: true, taxCode: true },
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    disabled: true,
    display: () => context.showSellerInfo,
  },
  seller_bank_account: {
    label: context.$t('invoice.seller_bank_account'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.seller_bank_account')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    display: () => context.showSellerInfo,
  },
  seller_bank_name: {
    hideLabel: true,
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.seller_bank_name')}`,
    type: 'input',
    display: () => context.showSellerInfo,
  },
  buyer_legal_name: {
    label: context.$t('invoice.buyer_legal_name'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_legal_name')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    display: () => context.showBuyerInfo && context.buyerType === BUYER_TYPE.ENTERPRISE,
    asyncValidation(value, resolve, formData) {
      let result = true;
      if (formData.buyer_type === BUYER_TYPE.ENTERPRISE && !formData.buyer_legal_name) {
        result = { msg: this.$t('invoice.buyer_legal_name_required') };
      }
      resolve(result);
    },
  },
  buyer_name: {
    label: context.$t('invoice.buyer_name'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_name')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    display: () => context.showBuyerInfo,
    asyncValidation(value, resolve, formData) {
      let result = true;
      if (formData.buyer_type === BUYER_TYPE.PERSONAL && !formData.buyer_name) {
        result = { msg: this.$t('invoice.buyer_name_required') };
      }
      resolve(result);
    },
  },
  buyer_phone_number: {
    label: context.$t('invoice.buyer_phone_number'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_phone_number')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    display: () => context.showBuyerInfo,
  },
  buyer_fax_number: {
    label: context.$t('invoice.buyer_fax_number'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_fax_number')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    labelClass: 'mb-4',
    display: () => context.showBuyerInfo,
  },
  buyer_email: {
    label: context.$t('invoice.buyer_email'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_email')}`,
    type: 'input',
    horizontalLayout: true,
    validation: 'email',
    className: 'width-10rem',
    labelClass: 'mb-4',
    display: () => context.showBuyerInfo,
  },
  buyer_address: {
    label: context.$t('invoice.buyer_address'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_address')}`,
    type: 'input',
    horizontalLayout: true,
    className: 'width-10rem',
    display: () => context.showBuyerInfo,
    asyncValidation(value, resolve, formData) {
      let result = true;
      if (formData.buyer_type === BUYER_TYPE.ENTERPRISE && !formData.buyer_address) {
        result = { msg: this.$t('invoice.buyer_address_required') };
      }
      resolve(result);
    },
    labelClass: 'mb-4',
  },
  buyer_taxcode: {
    label: context.$t('invoice.buyer_taxcode'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.buyer_taxcode')}`,
    type: 'autocomplete',
    display: () => context.showBuyerInfo,
    warningMessage: '',
    minSearchTextLength: 1,
    defaultMessage: context.$t('actions.form.buyer_taxcode.info'),
    fetchSuggestions(searchText) {
      if (context.buyerType === BUYER_TYPE.ENTERPRISE && regexTaxCode.test(searchText)) {
        const proxy = new InvoiceProxy();
        const settingsProxy = new SettingsProxy();

        // DB trước
        return settingsProxy.searchTaxCodeOnDB(searchText)
          .then((res) => {
            if (res && res.length > 0) {
              return res;
            }

            // fallback sang public
            return proxy.searchTaxCode(searchText);
          })
          .catch(() => {
            // nếu DB lỗi → public
            return proxy.searchTaxCode(searchText);
          });
      }
      return Promise.resolve([]);
    },
    renderItemText: (item) => {

      if (item.buyer_tax_code) {
        return `${item.buyer_tax_code} - ${item.buyer_name || item.buyer_legal_name}`;
      }

      if (item.company_TaxCode) {
        return `${item.company_TaxCode} - ${item.company}`;
      }
      if (item.text) {
        return item.text;
      }
      return item;
    },
    disabled: false,
    asyncValidation(value, resolve, formData) {
      let result = true;
      const rawValue = value || formData.buyer_taxcode;
      const taxCode =
        (rawValue && rawValue.company_TaxCode) ||
        (rawValue && rawValue.buyer_tax_code) ||
        (typeof rawValue === 'string' ? rawValue : '');
      if (formData.buyer_type === BUYER_TYPE.ENTERPRISE) {
        if (!taxCode) {
          result = { msg: this.$t('invoice.buyer_taxcode_required') };
        } else if (!regexTaxCode.test(taxCode)) {
          result = { msg: this.$t('error.form.taxCode.validate') };
        }
      }
      resolve(result);
    },
    horizontalLayout: true,
    className: 'width-10rem highlight-field',
    labelClass: 'mb-4',
  },
  buyer_id_no: {
    label: context.$t('actions.form.buyer_id_no.label'),
    placeholder: context.$t('actions.form.buyer_id_no.placeholder'),
    type: 'input',
    disabled: false,
    // display: () => context.showBuyerInfo,
    horizontalLayout: true,
    validation: { regex: regexNationalId },
    className: 'width-10rem',
    labelClass: 'mb-4',
  },
  buyer_passport_no: {
    label: context.$t('actions.form.buyer_passport_no.label'),
    placeholder: context.$t('actions.form.buyer_passport_no.placeholder'),
    type: 'input',
    disabled: false,
    // display: () => context.showBuyerInfo,
    horizontalLayout: true,
    validation: { regex: regexPassportNo },
    labelClass: 'mb-4',
  },
  buyer_type: {
    label: context.$t('invoice.buyer_type'),
    placeholder: `${context.$t('app.chooose')} ${context.$t('invoice.buyer_type')}`,
    type: 'select',
    display: () => context.showBuyerInfo,
    options: context.buyerTypeOptions,
    horizontalLayout: true,
  },
  mdvqhnsach: {
    label: context.$t('actions.form.mdvqhnsach.label'),
    placeholder: context.$t('actions.form.mdvqhnsach.placeholder'),
    type: 'input',
    disabled: false,
    horizontalLayout: true,
    validation: { regex: regexMdvqhnsach },
    labelClass: 'mb-4',
  },
  content: {
    label: context.$t('invoice.content'),
    placeholder: `${context.$t('app.enter')} ${context.$t('invoice.content')}`,
    type: 'textarea',
    validation: 'required',
  },
  kind_of: {
    label: context.$t('invoice.kind_of'),
    placeholder: `${context.$t('app.chooose')} ${context.$t('invoice.kind_of')}`,
    type: 'checkbox',
    disabled: true,
  },
  foreign_rate: {
    hideLabel: true,
    label: 'Tỷ giá',
    placeholder: 'Nhập tỷ giá',
    type: 'currency',
    display: () => context.showBuyerInfo,
    horizontalLayout: true,
    disabled: () => context.showExchangeRate,
    fractionDigits: () => context.renderFractionDigitsForeignRate,
    asyncValidation(value, resolve, formData) {
      let result = true;

      if (formData.currency_code !== 'VND' && (!formData.foreign_rate || formData.foreign_rate === 'NaN')) {
        result = { msg: 'Tỷ giá là bắt buộc' };
      }
      resolve(result);
    },
  },
  NBan_LDDNBo: {
    label: context.$t('actions.form.NBan_LDDNBo.label'),
    placeholder: context.$t('actions.form.NBan_LDDNBo.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    validation: 'required',
    horizontalLayout: true,
    labelClass: 'mb-4',
    className: 'width-10rem',
  },
  NBan_PTVChuyen: {
    label: context.$t('actions.form.NBan_PTVChuyen.label'),
    placeholder: context.$t('actions.form.NBan_PTVChuyen.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    validation: 'required',
    horizontalLayout: true,
    labelClass: 'mb-4',
    className: 'width-10rem',
  },
  NBan_HDSo: {
    label: context.$t('actions.form.NBan_HDSo.label'),
    placeholder: context.$t('actions.form.NBan_HDSo.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    horizontalLayout: true,
    labelClass: 'mb-4',
    className: 'width-10rem',
  },
  NBan_DChi: {
    label: context.$t('actions.form.NBan_DChi.label'),
    placeholder: context.$t('actions.form.NBan_DChi.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    horizontalLayout: true,
    labelClass: 'mb-4',
    className: 'width-10rem',
  },
  NBan_TNVChuyen: {
    label: context.$t('actions.form.NBan_TNVChuyen.label'),
    placeholder: context.$t('actions.form.NBan_TNVChuyen.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    horizontalLayout: true,
    labelClass: 'mb-4',
    className: 'width-10rem',
  },
  NBan_HVTNXHang: {
    label: context.$t('actions.form.NBan_HVTNXHang.label'),
    placeholder: context.$t('actions.form.NBan_HVTNXHang.placeholder'),
    type: 'input',
    display: () => context.showDeliveryInfo,
    horizontalLayout: true,
    labelClass: '',
    className: 'width-10rem',
  },
});
export default {
  name: 'CreateInvoiceForm',
  components: {
    AppForm,
    AppFormSubTitle,
  },
  props: {
    sellerSettingList: {
      type: Object,
      default: () => { },
    },
  },
  computed: {
    ...mapState(['invoiceListEnum', 'activeAnnouncementLines', 'companyMarked', 'sellerSettingList']),
    buyerTypeOptions() {
      return parseFormOption(this, 'BuyerTypeEnum');
    },
    paymentMethodOptions() {
      return parseFormOption(this, 'PaymentMethodEnum');
    },
    currencyCodeOptions() {
      return parseFormOption(this, 'CurrencyCodeEnum');
    },
    currentDate() {
      const currentDate = new Date();
      return `${this.$t('app.date')} ${currentDate.getDate()} ${this.$t('app.month').toLowerCase()} ${currentDate.getMonth() + 1} ${this.$t('app.year').toLowerCase()} ${currentDate.getFullYear()}`;
    },
    renderFractionDigitsForeignRate() {
      const setting = this.sellerSettingList.qty_rate__settings;
      if (setting && setting.length !== 0 && !this.showExchangeRate) {
        return setting.filter(s => s.key === 'RATE')[0].num_val || 0;
      } return 0;
    },
  },
  data() {
    return {
      schema: [],
      formModel: null,
      showBuyerInfo: true,
      showSellerInfo: false,
      showDeliveryInfo: false,
      showExchangeRate: true,
      buyerType: defaultFormModel.buyer_type,
      layout: {
        'div.row': {
          'div.col-md-6.pr-md-0': {
            field: 'invoice_type',
          },
          'div.offset-md-2.col-md-4.pr-0': {
            field: 'template_code',
          },
        },
        'div.row:0': {
          'div.offset-md-8.col-md-4.pr-0': {
            field: 'invoice_common_code',
          },
        },
        'div.row:1': {
          'div.col-12.text-center.mt-4.mb-2': {
            slot: 'title',
          },
        },
        'div.row:2': {
          'div.col-lg-12.pr-0': {
            slot: 'spacing-seller',
          },
        },
        'div.row:3': {
          'div.col-lg-12:1': {
            'div.row:2': {
              'div.col-lg-12.pr-0': {
                field: 'seller_name',
              },
            },
            'div.row:3': {
              'div.col-lg-12.pr-0': {
                field: 'seller_address',
              },
            },
            'div.row:4': {
              'div.col-lg-12.pr-0': {
                field: 'seller_taxcode',
              },
            },
            'div.row:5': {
              'div.col-sm-6.col-lg-4': {
                field: 'seller_bank_account',
              },
              'div.col-sm-6.col-lg-8.pr-0': {
                field: 'seller_bank_name',
              },
            },
          },
          'div.col-lg-12.pr-0:3': {
            slot: 'spacing-buyer',
          },
          'div.col-lg-12:2': {
            'div.row:0': {
              'div.col-lg-9.pr-0': {
                field: 'buyer_name',
              },
              'div.col-lg-3.pr-0': {
                field: 'buyer_type',
              },
            },
            'div.row:1': {
              'div.col-lg-12.pr-0': {
                field: 'buyer_legal_name',
              },
            },
            'div.row:2': {
              'div.col-lg-12.pr-0': {
                field: 'buyer_address',
              },
            },
            'div.row:3': {
              'div.col-lg-12.pr-0': {
                field: 'buyer_taxcode',
              },
            },
            'div.row:4': {
              'div.col-sm-4': {
                field: 'buyer_id_no',
              },
              'div.col-sm-4.px-0:2': {
                field: 'buyer_passport_no',
              },
              'div.col-sm-4.pr-0': {
                field: 'mdvqhnsach',
              },
            },
            'div.row:5': {
              'div.col-sm-6': {
                field: 'payment_method',
              },
              'div.col-sm-3.px-0:2': {
                field: 'currency_code',
              },
              'div.col-sm-3.pr-0': {
                field: 'foreign_rate',
              },
            },
          },
        },
        'div.row:5': {
          'div.col-lg-12': {
            slot: 'spacing-delivery',
          },
        },
        'div.row:6': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_LDDNBo',
          },
        },
        'div.row:7': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_PTVChuyen',
          },
        },
        'div.row:8': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_HDSo',
          },
        },
        'div.row:9': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_DChi',
          },
        },
        'div.row:10': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_TNVChuyen',
          },
        },
        'div.row:11': {
          'div.col-lg-12.pr-0': {
            field: 'NBan_HVTNXHang',
          },
        },
      },
      templateCodeOptions: [],
      invoiceTypeOptions: [],
      commonCodeOptions: [],
      invoiceTitle: this.$t('invoice.vatInvoice'),
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initialize();
    });
  },
  methods: {
    initialize() {
      this.schema = formSchema(this);
      this.initFormModel();
      this.$refs.form.initialize(this.formModel);
      const taxCodeField = this.$refs.form.getField('buyer_taxcode');
      if (taxCodeField) {
        taxCodeField.clear();
      }
    },
    initSellerFields() {
      const currentUser = JSON.parse(localStorage.getItem('currentUser'));
      const company = currentUser.company;

      this.formModel.buyer_type = this.sellerSettingList.buyer_type_recommended;
      this.buyerType = this.sellerSettingList.buyer_type_recommended;

      if (company) {
        this.formModel.seller_name = company.name;
        this.formModel.seller_address = company.company_address;
        this.formModel.seller_taxcode = company.tax_code;
        this.formModel.buyer_name = this.companyMarked.name;
        this.formModel.buyer_address = this.companyMarked.address;
        this.formModel.buyer_taxcode = this.companyMarked.tax_code ? {
          company: this.companyMarked.company_name,
          company_TaxCode: this.companyMarked.tax_code,
        } : null;
        this.formModel.buyer_legal_name = this.companyMarked.company_name;
      }
    },
    initFormModel() {
      this.formModel = Object.assign({}, defaultFormModel);
      // Mau Hoa Don
      this.formModel.invoice_type = (this.activeAnnouncementLines.list_invoice_type[0] || '');
      // Mau so
      this.formModel.template_code = (this.activeAnnouncementLines.list_template_code[this.formModel.invoice_type][0] || '');
      // Ký Hiệu Hoá Đơn
      this.formModel.invoice_common_code = (this.activeAnnouncementLines.list_common_code[this.formModel.template_code][0] || '');
      delete this.formModel.error;
      delete this.formModel.file_name;
      // Mau so
      const templateCode = (this.formModel && this.formModel.template_code) ? this.formModel.template_code : '';
      // Mau Hoa Don
      const invoiceType = (this.formModel && this.formModel.invoice_type) ? this.formModel.invoice_type : '';

      // Ký Hiệu Hoá Đơn
      this.commonCodeOptions = this.getCommonCodeOptions(templateCode);
      // Mau so
      this.templateCodeOptions = this.getTemplateCodeOptions(invoiceType);
      // Mau Hoa Don
      this.invoiceTypeOptions = this.activeAnnouncementLines.list_invoice_type.map(x =>
        ({ value: x, text: this.invoiceListEnum.InvoiceTypeEnum.find(a => (a.id === x)).lable }));

      this.$set(this.schema.invoice_type, 'options', this.invoiceTypeOptions);
      this.$set(this.schema.template_code, 'options', this.templateCodeOptions);
      this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
      this.initSellerFields();
    },
    getCommonCodeOptions(templateCode) {
      return templateCode ? this.activeAnnouncementLines.list_common_code[templateCode]
        .map((x, idx) => ({ id: idx, text: x, value: x })) : [];
    },
    getTemplateCodeOptions(invoiceType) {
      return invoiceType ?
        this.activeAnnouncementLines.list_template_code[invoiceType]
          .map((x, idx) => ({ id: idx, text: x, value: x })) : [];
    },
    getValues() {
      return this.$refs.form.getValues();
    },
    submit() {
      return this.$refs.form.submit();
    },
    validate() {
      return this.$refs.form.validate();
    },
    onFormFieldChanged($event, field) {
      const formVm = this.$refs.form;
      if (!field) {
        return;
      }

      if (field.name === 'buyer_type' && field.value) {
        this.buyerType = field.value;
        if (field.value === BUYER_TYPE.PERSONAL) {
          this.$set(this.schema, 'buyer_taxcode', {
            label: this.$t('invoice.buyer_taxcode'),
            placeholder: `${this.$t('app.enter')} ${this.$t('invoice.buyer_taxcode')}`,
            type: 'input',
            validation: { regex: regexTaxCode },
            horizontalLayout: true,
            className: 'width-10rem',
            labelClass: 'mb-4',
            disabled: false,
            display: () => this.showBuyerInfo,
          });
        } else this.$set(this.schema, 'buyer_taxcode', formSchema(this).buyer_taxcode);
      }

      if (field.name === 'buyer_taxcode' && field.value) {
        let warningMsg = '';
        const selectedCompany = field.value;

        if (selectedCompany.buyer_tax_code) {
          formVm.setFieldValue('buyer_legal_name', selectedCompany.buyer_name || selectedCompany.buyer_legal_name, { validate: false });
          formVm.setFieldValue('buyer_taxcode', selectedCompany.buyer_tax_code, { validate: false });
          formVm.setFieldValue('buyer_address', selectedCompany.buyer_address_line, { validate: false });
        }
        else if (selectedCompany.company_TaxCode) {
          formVm.setFieldValue('buyer_legal_name', selectedCompany.company, { validate: false });
          formVm.setFieldValue('buyer_taxcode', selectedCompany.company_TaxCode, { validate: false });
          formVm.setFieldValue('buyer_address', selectedCompany.company_address, { validate: false });
          if (selectedCompany.warning) {
            warningMsg = selectedCompany.company_situation;
          }
          if (selectedCompany.tax_risk) {
            if (warningMsg) {
              warningMsg += ' - ';
            }
            warningMsg += selectedCompany.tax_risk_status;
          }
        } else if (selectedCompany.text) {
          formVm.setFieldValue('buyer_taxcode', selectedCompany.text, { validate: false });
        }
        this.$set(this.schema.buyer_taxcode, 'warningMessage', warningMsg);
      }

      if (field.name === 'invoice_type') {
        this.formModel.invoice_type = field.value;
        this.showDeliveryInfo = field.value === '6';
        this.invoiceTitle = this.invoiceTypeOptions.find(i => i.value === this.formModel.invoice_type).text;

        const templateCode = (this.activeAnnouncementLines.list_template_code[this.formModel.invoice_type][0] || '');
        const commonCode = (this.activeAnnouncementLines.list_common_code[templateCode][0] || '');

        this.templateCodeOptions = this.getTemplateCodeOptions(this.formModel.invoice_type);
        this.$set(this.schema.template_code, 'options', this.templateCodeOptions);
        formVm.setFieldValue('template_code', templateCode, { validate: false });

        this.commonCodeOptions = this.getCommonCodeOptions(templateCode);
        this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
        formVm.setFieldValue('invoice_common_code', commonCode, { validate: false });

        formVm.setFieldValue('invoice_type', field.value, { validate: false });

        this.$emit('on-change-invoice-type', field.value);

        if (this.showDeliveryInfo) {
          formVm.setFieldValue('NBan_LDDNBo', null, { validate: false });
          formVm.setFieldValue('NBan_PTVChuyen', null, { validate: false });
          formVm.setFieldValue('NBan_HDSo', null, { validate: false });
          formVm.setFieldValue('NBan_DChi', null, { validate: false });
          formVm.setFieldValue('NBan_TNVChuyen', null, { validate: false });
          formVm.setFieldValue('NBan_HVTNXHang', null, { validate: false });
        }
      }
      if (field.name === 'template_code') {
        this.formModel.template_code = field.value;
        const commonCode = (this.activeAnnouncementLines.list_common_code[this.formModel.template_code][0] || '');

        this.commonCodeOptions = this.getCommonCodeOptions(this.formModel.template_code);
        this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
        formVm.setFieldValue('invoice_common_code', commonCode, { validate: false });

        formVm.setFieldValue('template_code', field.value, { validate: false });
      }
      if (field.name === 'total_amount_without_tax') {
        const vat = formVm.getFieldValue('tax_percentage') || 0;
        formVm.setFieldValue('total_amount_with_tax', `${Math.round(parseInt(field.value, 0) * ((100 + parseInt(vat, 0)) / 100))}`, { validate: false });
      }
      if (field.name === 'currency_code') {
        this.showExchangeRate = field.value === 'VND';
        if (this.showExchangeRate) formVm.setFieldValue('foreign_rate', null, { validate: false });
        this.$emit('on-change-currency-code', field.value);
      }
    },
    onFormFieldModified(isChanged) {
      this.$emit('on-input-modified', isChanged);
    },
    onSubmit(formModel, validate, { resolve, reject }) {
      this.$refs.form.validate().then((isValid) => {
        if (isValid) {
          this.$emit('on-submit', formModel, validate, { resolve, reject });
        }
      });
    },
  },
};
</script>

<style lang="scss">
.width-6rem {
  .label-wrapper {
    min-width: 6rem;
    width: 6rem;
  }
}

.width-10rem {
  .label-wrapper {
    min-width: 10rem;
    width: 10rem;
  }
}

.highlight-field {
  .form-control {
    border: 2px solid var(--primary-color);
  }
}
</style>



// WEBPACK FOOTER //
// src/portal-components/invoice/create-invoice-form.vue